import { httpsCallable } from 'firebase/functions';
import { getFunctions } from 'firebase/functions';
import app from '../../sdk_initialization';
import { sendEmail } from './emailService';
import { getEvent } from './eventsService';

const functions = getFunctions(app);

const normalizeConfiguredValue = (value) => {
  const normalized = String(value || '').trim();
  return normalized.toLowerCase() === 'na' ? '' : normalized;
};

const normalizePaymentMethodTypes = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((v) => String(v).trim())
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];

    // Support JSON arrays passed as strings, e.g. '["card","amazon_pay"]'
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((v) => String(v).trim())
            .filter(Boolean);
        }
      } catch (e) {
        // Fall through to comma-separated parsing.
      }
    }

    // Support comma-separated values, e.g. 'card,amazon_pay'
    return trimmed
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [];
};

/**
 * Create a Stripe Payment Intent
 * 
 * @param {Object} params - Payment parameters
 * @param {number} params.amount - Amount in cents (e.g., 1000 = $10.00)
 * @param {string} params.currency - Currency code (default: 'usd')
 * @param {string} params.description - Payment description
 * @param {Object} params.metadata - Optional metadata
 * @param {string} params.eventId - Optional eventId to allow server to select event-specific secret
 * @param {Array|string} params.payment_method_types - Supported payment method types
 * @param {string} params.payment_method_configuration - Payment method configuration ID
 * @returns {Promise<Object>} Payment intent details with client secret
 */
export const createPaymentIntent = async ({ amount, currency = 'usd', description, metadata = {}, eventId = '', payment_method_types, payment_method_configuration }) => {
  try {
    const createPaymentIntentFn = httpsCallable(functions, 'createPaymentIntent');
    const normalizedPaymentMethodTypes = normalizePaymentMethodTypes(payment_method_types);
    
    let resolvedPaymentMethodConfiguration = normalizeConfiguredValue(payment_method_configuration);
    if (!resolvedPaymentMethodConfiguration && eventId) {
      try {
        const event = await getEvent(eventId);
        const bazaarConfigId = normalizeConfiguredValue(event?.bazaarPaymentInfo?.paymentConfigId);
        if (bazaarConfigId) resolvedPaymentMethodConfiguration = bazaarConfigId;
      } catch (err) {
        console.warn('[createPaymentIntent] failed to resolve payment_method_configuration from event', { eventId, error: err });
      }
    }
    
    const result = await createPaymentIntentFn({
      amount,
      currency,
      description,
      metadata,
      eventId,
      ...(normalizedPaymentMethodTypes.length ? { payment_method_types: normalizedPaymentMethodTypes } : {}),
      ...(resolvedPaymentMethodConfiguration ? { payment_method_configuration: resolvedPaymentMethodConfiguration } : {}),
    });

    return result.data;
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw new Error(error.message || 'Failed to create payment intent');
  }
};

/**
 * Confirm a payment (retrieve payment status)
 * 
 * @param {string} paymentIntentId - Payment intent ID
 * @param {string} eventId - Optional eventId so server can use event-specific secret
 * @returns {Promise<Object>} Payment status
 */
export const confirmPayment = async (paymentIntentId, eventId = '') => {
  try {
    const confirmPaymentFn = httpsCallable(functions, 'confirmPayment');
    
    const result = await confirmPaymentFn({
      paymentIntentId,
      eventId
    });

    return result.data;
  } catch (error) {
    console.error('Error confirming payment:', error);
    throw new Error(error.message || 'Failed to confirm payment');
  }
};

/**
 * Finalize reservations after successful payment using backend authority.
 * This is provider-agnostic; provider verification happens server-side.
 */
export const finalizePaymentReservation = async ({
  paymentProvider = 'stripe',
  paymentReferenceId,
  eventId = '',
  layoutId = '',
  vendorId = '',
  createdByProfileId = '',
  sessionId = null,
  booths = [],
}) => {
  try {
    const finalizeFn = httpsCallable(functions, 'finalizePaymentReservation');
    const result = await finalizeFn({
      paymentProvider,
      paymentReferenceId,
      eventId,
      layoutId,
      vendorId,
      createdByProfileId,
      sessionId,
      booths,
      triggeredBy: 'Payment',
      type: 'Complete',
    });

    return result.data;
  } catch (error) {
    console.error('Error finalizing payment reservation:', error);
    throw new Error(error.message || 'Failed to finalize payment reservation');
  }
};

// 
export const sendPurchaseNotification = async (data) => {
  try {
    const toEmail = data.toEmail || data.metadata?.payeeEmail || data.metadata?.vendorEmail || '';
    const adminEmail = String(data.eventAdminContactEmail || '').trim() || 'bazaar@conv.icna.org';
    const subject = `Payment Confirmation - ${data.eventName || 'ICNA Event'}`;

    let purposeLine = null;
    if (data.purchaseType === 'attendees') {
      purposeLine = '<p><strong>Payment Purpose:</strong> Additional Attendee Purchase</p>';
    } else if (data.purchaseType === 'booths') {
      purposeLine = '<p><strong>Payment Purpose:</strong> Booth Purchase</p>';
    } else {
      purposeLine = '<p><strong>Payment Purpose:</strong> General Payment</p>';
    }

    let countLine = '';
    if (data.purchaseType === 'attendees' && data.attendeeCount) {
      countLine = `<p><strong>Additional Attendees Purchased:</strong> ${data.attendeeCount}</p>`;
    } else if (data.purchaseType === 'booths' && data.boothList) {
      countLine = `<p><strong>Booths Purchased:</strong> ${data.boothList}</p>`;
    } else {
      countLine = '<p><strong>Count of Items:</strong> Not Applicable</p>';
    }

    let body = `
      <p>Dear Vendor [${data.vendorName}],</p>
      <p>Thank you for your payment. See details below.</p>
      <p><strong>Event Name: </strong>${data.eventName || ''}</p>
      ${purposeLine}
      ${countLine}
      <p><strong>Total Transaction Amount:</strong> $${data.paidAmount}</p>
      <p><strong>Payment ID:</strong> ${data.paymentId}</p>
      <p>If you have any questions, please contact the Event Admin at ${adminEmail}.</p>
      <p>Thank you,<br/>Event Team</p>
    `;
    body += `<hr/><p>This is an automated message from ICNACON. Please do not reply to this email.</p>`;

    if (!toEmail) {
      console.warn('[sendPurchaseNotification] no recipient email; skipping send', { data });
      return false;
    }

    await sendEmail({ to: toEmail, subject, type: 'HTML', body });
  } catch (err) {
    console.error('[sendPurchaseNotification] failed to send email', { error: err });
    return false;
  }
  return true;
};

